/*
 * Turning the open email into a suggestion.
 *
 * The mapping is deliberately dumb for this release: subject becomes the
 * title, the latest message in the thread becomes the description. No
 * inference, no model. The user reviews and edits everything before anything
 * is created.
 *
 * What is not dumb is the envelope it comes back in. Every value carries the
 * confidence behind it and the part of the email it came from, and the whole
 * message context travels alongside — see lib/suggestion.ts.
 */

/* global Office */

import { latestMessage } from "./thread";
import {
  FIELD_DESCRIPTION,
  FIELD_PROJECT_ID,
  FIELD_TITLE,
  SOURCE_BODY,
  SOURCE_SUBJECT,
  absent,
  proposed,
  type MessageContext,
  type Suggestion,
} from "./suggestion";

/**
 * The gateway caps ticket titles at 70 characters. That cap is a .NET string
 * length, measured in UTF-16 code units — the same thing JavaScript's .length
 * measures — so counting with .length here agrees exactly with the server and
 * with the gateway.
 */
export const TITLE_MAX_LENGTH = 70;

/** Matches the BFF's descriptionMaxBytes so the user is not surprised by a 400. */
export const DESCRIPTION_MAX_LENGTH = 32 * 1024;

/**
 * Collapses the whitespace an email subject collects (line breaks from long
 * headers, runs of spaces) and truncates to the gateway's limit.
 *
 * The ellipsis is deliberate: a silently shortened title looks like the user
 * typed it that way, and they would only find out when someone asked why the
 * ticket title stops mid-word. Nothing is lost by the cut — the whole subject
 * travels on in the message context.
 */
export function titleFromSubject(subject: string): string {
  const clean = (subject ?? "").replace(/\s+/g, " ").trim();
  if (clean.length <= TITLE_MAX_LENGTH) {
    return clean;
  }
  let cut = clean.slice(0, TITLE_MAX_LENGTH - 1);
  // Slicing by code unit can strand the high half of a surrogate pair, which
  // renders as a replacement character. Drop it if that happened.
  const lastCode = cut.charCodeAt(cut.length - 1);
  if (lastCode >= 0xd800 && lastCode <= 0xdbff) {
    cut = cut.slice(0, -1);
  }
  return cut.trimEnd() + "…";
}

/**
 * Normalises an email body for the description field: CRLF to LF, no more
 * than one blank line in a row, trimmed, and capped.
 *
 * Body.getAsync is asked for Text rather than Html, so there is no markup to
 * strip here — but plain-text conversions of HTML mail arrive full of blank
 * lines, and those make a ticket unreadable.
 */
export function descriptionFromBody(body: string): string {
  const clean = (body ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return clean.length > DESCRIPTION_MAX_LENGTH ? clean.slice(0, DESCRIPTION_MAX_LENGTH) : clean;
}

/**
 * Formats a sender the way RFC 5322 writes one, so a single string carries
 * both halves.
 *
 * The display name is worth keeping and not obvious: on mail from a site crew
 * it is often the only place a person's actual name appears, the address being
 * something like noreply@ or a shared account.
 */
export function senderOf(
  from: { emailAddress?: string; displayName?: string } | undefined
): string {
  const address = asText(from?.emailAddress).trim();
  const name = asText(from?.displayName).trim();
  if (!name || name === address) {
    return address;
  }
  return address ? `${name} <${address}>` : name;
}

/**
 * Formats Office's dateTimeCreated as RFC 3339.
 *
 * It is a Date, not a string — which is easy to miss, because logging the item
 * shows an ISO string: that is JSON.stringify calling the Date's toJSON. Left
 * unconverted it would reach the server as "[object Date]", and the one thing
 * that can ever resolve a "yesterday" written in a body would be gone.
 */
export function asTimestamp(value: unknown): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  }
  return typeof value === "string" ? value : "";
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** Reports whether an Office host has initialised and exposes a mail item. */
export function hasMailItem(): boolean {
  try {
    return typeof Office !== "undefined" && Boolean(Office.context?.mailbox?.item);
  } catch {
    return false;
  }
}

function readBody(item: Office.MessageRead): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value ?? "");
      } else {
        reject(new Error(result.error?.message ?? "Could not read the message body."));
      }
    });
  });
}

/**
 * Reads the open message into a suggestion.
 *
 * Rejects rather than returning partial values, so the caller can leave the
 * form empty and say why instead of silently prefilling half of it.
 */
export async function readSuggestion(): Promise<Suggestion> {
  const item = Office.context.mailbox.item;
  if (!item) {
    throw new Error("No message is open.");
  }

  const subject = asText(item.subject).trim();
  const body = await readBody(item);

  const title = titleFromSubject(subject);
  const description = descriptionFromBody(latestMessage(body));

  const messageContext: MessageContext = {
    messageId: asText(item.internetMessageId),
    receivedAt: asTimestamp(item.dateTimeCreated),
    from: senderOf(item.from),
    // The full subject, not the truncated title: this is what makes the
    // 70-character cap cost no information.
    subject,
  };

  return {
    fields: {
      // An email with no subject proposed nothing, which is a different fact
      // from proposing an empty title.
      [FIELD_TITLE]: title ? proposed(title, SOURCE_SUBJECT) : absent(),
      [FIELD_DESCRIPTION]: description ? proposed(description, SOURCE_BODY) : absent(),
      // No email names a Valoon project, so nothing is ever proposed here.
      // That stays true when a model arrives: it will extract the mention as
      // written, and our code will match it against the real catalogue.
      [FIELD_PROJECT_ID]: absent(),
    },
    messageContext,
  };
}
